/**
 * Cliente HTTP do front — o único lugar que chama `fetch`.
 *
 * Papel: monta a URL, injeta o token do Auth0, envia, valida a resposta com o
 * schema do contrato e converte qualquer falha em `ErroDaApi`.
 *
 * Como usar: todo service de módulo deverá chamar a API pelo `clienteApi`, e
 * nunca por `fetch` direto. Concentrar a chamada aqui é o que torna automáticos
 * o cabeçalho `Authorization`, o formato único de erro e a conferência da
 * resposta contra o schema; um `fetch` fora deste arquivo perderia os três sem
 * que nada acusasse, e por isso é violação registrada no CLAUDE.md.
 *
 * Em produção a requisição sai para a origem do Function App, que é outro
 * domínio: o navegador faz antes um _preflight_ `OPTIONS`, e só segue se a
 * origem do front estiver no CORS do Function App. Um endereço novo do front
 * que não entre nesse CORS falha em toda chamada com erro de rede, sem status.
 *
 * A validação da resposta com Zod custa alguns milissegundos: sem ela, o tipo
 * de retorno seria uma promessa não verificada, e uma mudança de contrato no
 * servidor apareceria como `undefined` em algum canto da tela, longe da causa.
 * Com ela, o erro aponta o campo e a rota.
 *
 * O token é injetado por registro, e não importado: `getAccessTokenSilently`
 * vem de um hook do React e não pode ser chamado de um módulo solto. O
 * `ProvedorAuth` chama `configurarObtencaoDeToken` assim que monta, e o preço é
 * um estado mutável de módulo.
 */
import type { z } from 'zod';

import { CODIGOS_ERRO, respostaErroSchema } from '@concierge/shared';
import { lerBaseDaApi } from '@/shared/lib/ambiente';
import { ErroDaApi, erroDeRede } from './erros';

/**
 * Prefixo de toda rota: a origem do Function App seguida de `/api` em
 * produção, ou `/api` relativo em desenvolvimento.
 *
 * É calculado uma vez, no carregamento, porque a `VITE_API_URL` é embutida no
 * bundle e não muda enquanto a página estiver aberta.
 */
const BASE = lerBaseDaApi();

/** Valores aceitos como parâmetro de busca. `undefined` é omitido da URL. */
type ValorDeParametro = string | number | boolean | undefined;

interface OpcoesComuns {
  /** Parâmetros de busca. Chave com valor `undefined` não entra na URL. */
  params?: Record<string, ValorDeParametro>;
  /** Sinal de cancelamento, repassado pelo TanStack Query. */
  sinal?: AbortSignal;
  /**
   * Dispensa o token. Só para rota que a API expõe sem autenticação, como
   * `/api/saude`. O padrão é exigir token, de propósito: esquecer de marcar
   * uma rota como pública gera um 401 visível; esquecer de marcar uma rota
   * como privada não geraria erro nenhum.
   */
  publico?: boolean;
}

interface OpcoesComSchema<T extends z.ZodType> extends OpcoesComuns {
  /** Schema de `@concierge/shared` que descreve a resposta esperada. */
  schema: T;
}

let obterToken: (() => Promise<string>) | null = null;

/**
 * Registra a função que fornece o token de acesso.
 *
 * Chamada uma vez pelo `ProvedorAuth`, no momento em que o Auth0 termina de
 * inicializar. Chamadas posteriores substituem a anterior, o que é o
 * comportamento desejado quando o React remonta a árvore em desenvolvimento.
 *
 * @param fornecedor - Função que devolve um token válido, renovando se preciso.
 */
export function configurarObtencaoDeToken(fornecedor: () => Promise<string>): void {
  obterToken = fornecedor;
}

/**
 * Monta a URL completa a partir do caminho e dos parâmetros.
 *
 * Parâmetro `undefined` é omitido em vez de virar a string `"undefined"` na
 * query — engano comum, e que do lado da API chega como um filtro com valor
 * literal `undefined` em vez de filtro ausente.
 *
 * @param caminho - Caminho relativo à API, começando com barra.
 * @param params - Parâmetros de busca, possivelmente com valores ausentes.
 * @returns URL pronta, com a query string já codificada.
 */
function montarUrl(caminho: string, params?: Record<string, ValorDeParametro>): string {
  const url = `${BASE}${caminho}`;
  if (params === undefined) return url;

  const busca = new URLSearchParams();
  for (const [chave, valor] of Object.entries(params)) {
    if (valor !== undefined) busca.set(chave, String(valor));
  }

  const query = busca.toString();
  return query === '' ? url : `${url}?${query}`;
}

/**
 * Monta os cabeçalhos da requisição, incluindo o token quando exigido.
 *
 * @param temCorpo - Se a requisição envia corpo JSON.
 * @param publico - Se a rota dispensa autenticação.
 * @param aceita - O `Accept` da requisição: JSON em toda rota, menos na que
 *   devolve um arquivo, que aceita qualquer tipo.
 * @returns Cabeçalhos prontos para o `fetch`.
 * @throws `ErroDaApi` 401 se o token for exigido e ainda não houver fornecedor
 *   registrado, ou se a renovação silenciosa falhar.
 */
async function montarCabecalhos(
  temCorpo: boolean,
  publico: boolean,
  aceita = 'application/json',
): Promise<HeadersInit> {
  const cabecalhos: Record<string, string> = { Accept: aceita };
  if (temCorpo) cabecalhos['Content-Type'] = 'application/json';
  if (publico) return cabecalhos;

  if (obterToken === null) {
    throw new ErroDaApi(
      CODIGOS_ERRO.NAO_AUTENTICADO,
      401,
      'Sessão ainda não iniciada. Recarregue a página.',
    );
  }

  try {
    cabecalhos.Authorization = `Bearer ${await obterToken()}`;
  } catch {
    // Renovação silenciosa falha quando o refresh token expirou ou foi
    // revogado. Do ponto de vista de quem usa o sistema é o mesmo que sessão
    // encerrada. O erro sobe com o código NAO_AUTENTICADO e quem oferece a
    // volta ao login é a tela: este arquivo é `shared` e não alcança o roteador
    // nem o provedor do Auth0, que vivem em `app`. Redirecionar daqui também
    // descartaria, sem aviso, o formulário que estivesse preenchido na tela.
    throw new ErroDaApi(
      CODIGOS_ERRO.NAO_AUTENTICADO,
      401,
      'Sessão expirada. Entre novamente.',
    );
  }

  return cabecalhos;
}

/**
 * Converte uma resposta fora da faixa de sucesso em `ErroDaApi`.
 *
 * Tenta ler o envelope de erro do contrato. Quando o corpo não é esse envelope
 * — um 502 do proxy, uma página de erro em HTML — cai num erro genérico com o
 * status recebido, em vez de deixar vazar HTML na tela como se fosse mensagem.
 *
 * @param resposta - Resposta com status de erro.
 * @returns Erro pronto para lançar.
 */
async function converterRespostaEmErro(resposta: Response): Promise<ErroDaApi> {
  let corpo: unknown;

  try {
    corpo = await resposta.json();
  } catch {
    corpo = null;
  }

  const analisado = respostaErroSchema.safeParse(corpo);

  if (analisado.success) {
    const { codigo, mensagem, campos } = analisado.data.erro;
    // O código vem do servidor como texto livre do ponto de vista do tipo;
    // conferir contra a tabela evita que um código novo, ainda desconhecido
    // do front, atravesse como se fosse um dos previstos.
    const conhecido = Object.values(CODIGOS_ERRO).find((valor) => valor === codigo);
    return new ErroDaApi(
      conhecido ?? CODIGOS_ERRO.ERRO_INTERNO,
      resposta.status,
      mensagem,
      campos,
    );
  }

  return new ErroDaApi(
    CODIGOS_ERRO.ERRO_INTERNO,
    resposta.status,
    'A operação falhou. Tente novamente em instantes.',
  );
}

/**
 * Executa a requisição e devolve o corpo já validado.
 *
 * @param metodo - Verbo HTTP.
 * @param caminho - Caminho relativo à API, começando com barra.
 * @param schema - Schema do contrato que descreve a resposta esperada.
 * @param corpo - Objeto a enviar como JSON, quando o verbo aceitar corpo.
 * @param opcoes - Parâmetros de busca, sinal de cancelamento e flag de rota
 *   pública.
 * @returns O corpo da resposta validado pelo schema.
 * @throws `ErroDaApi` em falha de rede, status de erro, ou resposta que não
 *   corresponde ao schema.
 */
async function requisitar<T extends z.ZodType>(
  metodo: string,
  caminho: string,
  schema: T,
  corpo: unknown,
  opcoes: OpcoesComuns,
): Promise<z.infer<T>> {
  const temCorpo = corpo !== undefined;
  const cabecalhos = await montarCabecalhos(temCorpo, opcoes.publico === true);

  let resposta: Response;

  try {
    resposta = await fetch(montarUrl(caminho, opcoes.params), {
      method: metodo,
      headers: cabecalhos,
      body: temCorpo ? JSON.stringify(corpo) : undefined,
      signal: opcoes.sinal,
    });
  } catch {
    throw erroDeRede();
  }

  if (!resposta.ok) throw await converterRespostaEmErro(resposta);

  const bruto: unknown = await resposta.json();
  const analisado = schema.safeParse(bruto);

  if (!analisado.success) {
    throw new ErroDaApi(
      CODIGOS_ERRO.ERRO_INTERNO,
      resposta.status,
      'O servidor devolveu uma resposta em formato inesperado.',
    );
  }

  return analisado.data;
}

/**
 * Métodos HTTP que os services de módulo chamam.
 *
 * Todo service de módulo passa por aqui. Os métodos com corpo recebem o objeto
 * antes das opções, seguindo a ordem em que se lê a operação: para onde, o
 * quê, e como.
 */
export const clienteApi = {
  /**
   * Busca um recurso ou uma listagem.
   *
   * @param caminho - Caminho relativo à API, começando com barra.
   * @param opcoes - Schema da resposta e, opcionalmente, parâmetros de busca.
   * @returns O corpo validado pelo schema.
   */
  get<T extends z.ZodType>(caminho: string, opcoes: OpcoesComSchema<T>): Promise<z.infer<T>> {
    return requisitar('GET', caminho, opcoes.schema, undefined, opcoes);
  },

  /**
   * Cria um recurso.
   *
   * @param caminho - Caminho relativo à API, começando com barra.
   * @param corpo - Objeto a enviar, já validado pelo schema do formulário.
   * @param opcoes - Schema da resposta e opções da requisição.
   * @returns O recurso criado, como ficou gravado.
   */
  post<T extends z.ZodType>(
    caminho: string,
    corpo: unknown,
    opcoes: OpcoesComSchema<T>,
  ): Promise<z.infer<T>> {
    return requisitar('POST', caminho, opcoes.schema, corpo, opcoes);
  },

  /**
   * Substitui um recurso por inteiro.
   *
   * @param caminho - Caminho relativo à API, começando com barra.
   * @param corpo - Estado completo do recurso após a edição.
   * @param opcoes - Schema da resposta e opções da requisição.
   * @returns O recurso como ficou gravado.
   */
  put<T extends z.ZodType>(
    caminho: string,
    corpo: unknown,
    opcoes: OpcoesComSchema<T>,
  ): Promise<z.infer<T>> {
    return requisitar('PUT', caminho, opcoes.schema, corpo, opcoes);
  },

  /**
   * Altera parte de um recurso.
   *
   * @param caminho - Caminho relativo à API, começando com barra.
   * @param corpo - Apenas os campos que mudam.
   * @param opcoes - Schema da resposta e opções da requisição.
   * @returns O recurso como ficou gravado.
   */
  patch<T extends z.ZodType>(
    caminho: string,
    corpo: unknown,
    opcoes: OpcoesComSchema<T>,
  ): Promise<z.infer<T>> {
    return requisitar('PATCH', caminho, opcoes.schema, corpo, opcoes);
  },

  /**
   * Envia um corpo a uma rota que responde 204, sem corpo de volta.
   *
   * É o caminho de `POST /financas/lancamentos/reordenar` e de
   * `PUT /financas/categorias/{id}/cor`: as duas gravam e não têm o que
   * devolver. Passar por `post` ou `put` exigiria um schema e tentaria ler JSON
   * de uma resposta vazia, e a gravação que deu certo apareceria como falha.
   *
   * @param metodo - `POST` ou `PUT`.
   * @param caminho - Caminho relativo à API, começando com barra.
   * @param corpo - Objeto a enviar, já validado pelo schema do contrato.
   * @param opcoes - Opções da requisição.
   */
  async enviar(
    metodo: 'POST' | 'PUT',
    caminho: string,
    corpo: unknown,
    opcoes: OpcoesComuns = {},
  ): Promise<void> {
    const cabecalhos = await montarCabecalhos(true, opcoes.publico === true);

    let resposta: Response;

    try {
      resposta = await fetch(montarUrl(caminho, opcoes.params), {
        method: metodo,
        headers: cabecalhos,
        body: JSON.stringify(corpo),
        signal: opcoes.sinal,
      });
    } catch {
      throw erroDeRede();
    }

    if (!resposta.ok) throw await converterRespostaEmErro(resposta);
  },

  /**
   * Baixa um arquivo — uma imagem, um PDF — como `Blob`.
   *
   * É o caminho de `GET /planos-saude/{id}/foto`, que devolve os bytes da
   * imagem e não JSON. A rota exige o token, e por isso o endereço dela não
   * serve direto no `src` de um `<img>`: o navegador não manda o
   * `Authorization` sozinho, e a imagem voltaria 401. Quem desenha a imagem
   * deverá passar o `Blob` por `URL.createObjectURL` e revogar a URL quando ela
   * deixar de ser usada; a revogação é de quem cria a URL, e não daqui.
   *
   * O erro chega como nos outros métodos: o 401 da sessão expirada, o 404 do
   * arquivo que não existe e o 503 do armazenamento fora do ar viram
   * `ErroDaApi` com a mensagem do servidor, porque a resposta de erro continua
   * sendo o envelope JSON do contrato.
   *
   * @param caminho - Caminho relativo à API, começando com barra.
   * @param opcoes - Parâmetros de busca e sinal de cancelamento.
   * @returns O corpo da resposta, com o `type` que o `Content-Type` declarou.
   */
  async baixar(caminho: string, opcoes: OpcoesComuns = {}): Promise<Blob> {
    const cabecalhos = await montarCabecalhos(false, opcoes.publico === true, '*/*');

    let resposta: Response;

    try {
      resposta = await fetch(montarUrl(caminho, opcoes.params), {
        method: 'GET',
        headers: cabecalhos,
        signal: opcoes.sinal,
      });
    } catch {
      throw erroDeRede();
    }

    if (!resposta.ok) throw await converterRespostaEmErro(resposta);

    try {
      return await resposta.blob();
    } catch {
      throw erroDeRede();
    }
  },

  /**
   * Exclui um recurso.
   *
   * Não recebe schema, e por isso não passa por `requisitar`: a API responde
   * 204 sem corpo, e tentar interpretar o corpo vazio como JSON lançaria um
   * erro de parse justamente no caminho de sucesso.
   *
   * @param caminho - Caminho relativo à API, começando com barra.
   * @param opcoes - Opções da requisição.
   */
  async remover(caminho: string, opcoes: OpcoesComuns = {}): Promise<void> {
    const cabecalhos = await montarCabecalhos(false, opcoes.publico === true);

    let resposta: Response;

    try {
      resposta = await fetch(montarUrl(caminho, opcoes.params), {
        method: 'DELETE',
        headers: cabecalhos,
        signal: opcoes.sinal,
      });
    } catch {
      throw erroDeRede();
    }

    if (!resposta.ok) throw await converterRespostaEmErro(resposta);
  },
};
